export interface FontOption {
	id: string;
	name: string;
	description: string;
	/** CSS font-family stack applied to --font-ui */
	cssStack: string;
	/** Google Fonts stylesheet URL, undefined for system/already-loaded fonts */
	googleFontsUrl?: string;
}

export const FONT_OPTIONS: readonly FontOption[] = [
	{
		id: 'system',
		name: 'System',
		description: "Your platform's native font — SF Pro, Segoe UI, or Roboto",
		cssStack:
			'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif, "Noto Color Emoji"',
	},
	{
		id: 'geist',
		name: 'Geist',
		description: 'Minimal and precise — designed for dense UI, readable at 13px without being clinical',
		cssStack: '"Geist", ui-sans-serif, system-ui, sans-serif',
		googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Geist:wght@300..700&display=swap',
	},
	{
		id: 'figtree',
		name: 'Figtree',
		description: 'Clean geometric sans — fresh and editorial, reads well in tight columns',
		cssStack: '"Figtree", ui-sans-serif, system-ui, sans-serif',
		googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap',
	},
	{
		id: 'nunito-sans',
		name: 'Nunito Sans',
		description: 'Round and warm — approachable personality without sacrificing readability',
		cssStack: '"Nunito Sans", ui-sans-serif, system-ui, sans-serif',
		googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,300..700;1,6..12,400&display=swap',
	},
	{
		id: 'barlow',
		name: 'Barlow',
		description: 'Humanist sans — the original SkyDeck font, informal yet clear',
		cssStack: '"Barlow", ui-sans-serif, system-ui, sans-serif',
		googleFontsUrl:
			'https://fonts.googleapis.com/css2?family=Barlow:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap',
	},
];

export const DEFAULT_FONT_ID = 'system';

const LOADED = new Set<string>();

/** Resolve a font id to its option, falling back to the first (system) option. */
export const resolveFontOption = (id: string): FontOption => {
	return FONT_OPTIONS.find((f) => f.id === id) ?? FONT_OPTIONS[0];
};

/**
 * Load the Google Font stylesheet for a font (once) without changing the active
 * UI font. Used to warm the preview on hover so it renders promptly when picked.
 */
export const preloadFont = (id: string): void => {
	const option = resolveFontOption(id);

	if (option.googleFontsUrl && !LOADED.has(id)) {
		LOADED.add(id);
		const link = document.createElement('link');
		link.rel = 'stylesheet';
		link.href = option.googleFontsUrl;
		document.head.appendChild(link);
	}
};

/** Apply the chosen font — loads the Google Font stylesheet once and sets --font-ui on :root. */
export const applyFont = (id: string): void => {
	const option = resolveFontOption(id);

	preloadFont(id);

	document.documentElement.style.setProperty('--font-ui', option.cssStack);
};
